import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import types from '../mutation-types';

export const state = {
  records: [],
  recordsById: {},
  activeFilters: {},
  pendingActionsById: {},
  // Ciclos por campanha, cache do `GET /campaigns/:id/cycles`. Vive na store e
  // nao no card porque `campaign.updated` chega para toda a lista: quinze cards
  // recorrentes com cache local pediriam os ciclos quinze vezes por evento.
  cyclesByCampaignId: {},
  meta: {
    count: 0,
    current_page: 1,
    status_counts: {},
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getCampaigns: _state => campaignType => {
    // O backend filtra por params[:campaign_type], mas o getter não pode
    // depender disso: basta um `campaigns/get` sem parâmetro para o state
    // ficar com todos os tipos, e a aba passa a mostrar campanha alheia.
    // Foi o que acontecia — Index.vue despachava sem filtro no mounted e,
    // como mounted do pai roda DEPOIS do filho, a resposta não filtrada
    // sobrescrevia a filtrada.
    return _state.records.filter(
      campaign => campaign.campaign_type === campaignType
    );
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
  getCampaignById: _state => campaignId => {
    return _state.recordsById[campaignId];
  },
  getPendingAction: _state => campaignId => {
    return _state.pendingActionsById?.[campaignId] || null;
  },
  // Contagem por status do tipo pedido, ja com a busca e o periodo aplicados e
  // SEM o filtro de status -- e por isso que o dropdown mostra quantas
  // campanhas cada status tem mesmo com um status ja selecionado.
  getStatusCounts(_state) {
    return _state.meta?.status_counts || {};
  },
  getCampaignCycles: _state => campaignId => {
    return (
      _state.cyclesByCampaignId?.[campaignId] || {
        records: [],
        meta: { count: 0, current_page: 1, total_pages: 0, per_page: 10 },
        isFetching: false,
        hasError: false,
      }
    );
  },
};

const latestCyclesRequest = {};

export const actions = {
  get: async function getCampaigns({ commit }, params = {}) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    commit(types.SET_CAMPAIGN_FILTERS, params);
    try {
      const response = await CampaignsAPI.get(params);
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },

  getCampaign: async ({ commit }, { id }) => {
    try {
      const response = await CampaignsAPI.getCampaign(id);
      commit(types.UPDATE_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    }
  },

  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, campaignObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, campaignObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  async getFailedDeliveries({ commit }, { campaign_id, ...params }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.getFailedDeliveries(
        campaign_id,
        params
      );
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  async getDeliveries({ commit }, { campaign_id, ...params }) {
    try {
      return await CampaignsAPI.getDeliveries(campaign_id, params);
    } catch (error) {
      throw error;
    }
  },
  async getAuditEvents({ commit }, { campaign_id, ...params }) {
    try {
      return await CampaignsAPI.getAuditEvents(campaign_id, params);
    } catch (error) {
      throw error;
    }
  },
  async exportDeliveries({ commit }, { campaign_id, ...params }) {
    try {
      return await CampaignsAPI.exportDeliveries(campaign_id, params);
    } catch (error) {
      throw error;
    }
  },
  async stop({ commit }, { id }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.stop(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  async pause({ commit }, { id }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    commit(types.SET_CAMPAIGN_PENDING_ACTION, { id, action: 'pausing' });
    try {
      const response = await CampaignsAPI.pause(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      commit(types.SET_CAMPAIGN_PENDING_ACTION, { id, action: null });
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  async resume({ commit }, { id }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    commit(types.SET_CAMPAIGN_PENDING_ACTION, { id, action: 'resuming' });
    try {
      const response = await CampaignsAPI.resume(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      commit(types.SET_CAMPAIGN_PENDING_ACTION, { id, action: null });
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  // Navegar pelas janelas de ciclo dispara pedidos em sequencia para a mesma
  // campanha, e a resposta lenta de uma pagina antiga chegava depois da nova e a
  // sobrescrevia. So o pedido mais recente de cada campanha escreve na store; o
  // vencido devolve null para o chamador nao agir sobre ele.
  async getCycles({ commit }, { id, page = 1, perPage = 10 }) {
    latestCyclesRequest[id] = (latestCyclesRequest[id] || 0) + 1;
    const requestId = latestCyclesRequest[id];
    const isCurrent = () => latestCyclesRequest[id] === requestId;

    commit(types.SET_CAMPAIGN_CYCLES, {
      id,
      isFetching: true,
      hasError: false,
    });
    try {
      const response = await CampaignsAPI.getCycles(id, {
        page,
        per_page: perPage,
      });
      if (!isCurrent()) return null;
      commit(types.SET_CAMPAIGN_CYCLES, {
        id,
        records: response.data?.payload || [],
        meta: response.data?.meta || {},
        isFetching: false,
      });
      return response;
    } catch (error) {
      // 422 e resposta esperada: a campanha deixou de ser recorrente entre a
      // pintura do card e a requisicao. O card cai para a barra simples.
      if (!isCurrent()) return null;
      commit(types.SET_CAMPAIGN_CYCLES, {
        id,
        isFetching: false,
        hasError: true,
      });
      throw error;
    }
  },
  updateCampaign({ commit }, campaign) {
    commit(types.EDIT_CAMPAIGN, campaign);
  },
};

// O filtro de status deixou de ser so do envio em massa: cada tipo tem o seu
// campo (status_envia inteiro, campaign_status nome, enabled booleano). Comparar
// tudo com `status_envia`, como antes, tirava da lista toda campanha de SMS e de
// website assim que um `campaign.updated` chegava com filtro de status ativo --
// `Number(undefined) !== Number('scheduled')` e sempre verdadeiro.
const matchesStatusFilter = (campaign, filters) => {
  const { status, campaign_type: campaignType } = filters;
  if (status === '' || status === undefined || status === null) return true;

  if (campaignType === 'ongoing') {
    return status === (campaign.enabled ? 'enabled' : 'disabled');
  }
  if (campaignType === 'one_off') {
    return campaign.campaign_status === status;
  }
  if (campaignType === 'mass_outbound') {
    return Number(campaign.status_envia) === Number(status);
  }

  return true;
};

const campaignMatchesFilters = (campaign, filters = {}) => {
  if (filters.campaign_type && campaign.campaign_type !== filters.campaign_type)
    return false;
  if (!matchesStatusFilter(campaign, filters)) return false;
  if (
    filters.q &&
    !campaign.title?.toLowerCase().includes(filters.q.toLowerCase())
  )
    return false;
  if (filters.since && filters.until) {
    const createdAt = Math.floor(
      new Date(campaign.created_at).getTime() / 1000
    );
    if (createdAt < filters.since || createdAt > filters.until) return false;
  }
  return true;
};

export const mutations = {
  [types.SET_CAMPAIGN_PENDING_ACTION]: (state, { id, action }) => {
    const pendingActions = state.pendingActionsById || {};
    if (action) {
      state.pendingActionsById = {
        ...pendingActions,
        [id]: action,
      };
      return;
    }

    const { [id]: _removedAction, ...remainingActions } = pendingActions;
    state.pendingActionsById = remainingActions;
  },
  [types.SET_CAMPAIGN_CYCLES]: (state, { id, records, meta, ...flags }) => {
    const current = state.cyclesByCampaignId?.[id] || {
      records: [],
      meta: { count: 0, current_page: 1, total_pages: 0, per_page: 10 },
    };
    state.cyclesByCampaignId = {
      ...(state.cyclesByCampaignId || {}),
      [id]: {
        ...current,
        // `records`/`meta` ausentes preservam o que ja estava: enquanto a
        // pagina nova carrega, a barra continua mostrando a anterior em vez de
        // piscar vazia.
        ...(records ? { records } : {}),
        ...(meta ? { meta: { ...current.meta, ...meta } } : {}),
        ...flags,
      },
    };
  },
  [types.SET_CAMPAIGN_FILTERS]: (state, filters) => {
    state.activeFilters = { ...filters };
  },
  [types.SET_CAMPAIGNS]: (state, data) => {
    if (data.payload) {
      state.records = data.payload;
      // `status_counts` cai para {} quando o backend nao manda (deploy em que a
      // imagem nova do front chega antes da do back): sem o default o dropdown
      // lia `undefined[status]` e nao pintava contagem nenhuma.
      state.meta = { status_counts: {}, ...(data.meta || {}) };
    } else {
      state.records = data;
    }
    state.recordsById = state.records.reduce(
      (records, campaign) => ({ ...records, [campaign.id]: campaign }),
      { ...(state.recordsById || {}) }
    );
    state.uiFlags.isFetching = false;
  },
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  // Campanha nova entra no TOPO, e nao no fim.
  //
  // Com `push` ela caia na ultima posicao da pagina 1 -- e a ordenacao padrao e
  // data de criacao DECRESCENTE, ou seja, o lugar dela e o primeiro. Quem criava
  // uma campanha ficava olhando uma lista que nao mudou (o item entrou 15 linhas
  // abaixo, fora da dobra) e criava de novo.
  //
  // Ela tambem precisa BATER com os filtros ativos: criar uma campanha de SMS
  // com o filtro "Finalizada" ligado a punha na lista como se ela tivesse
  // finalizado. O `count` sobe junto, senao o rodape diz "de 24" com 25 na tela.
  [types.ADD_CAMPAIGN]: (state, campaign) => {
    state.recordsById = {
      ...(state.recordsById || {}),
      [campaign.id]: campaign,
    };

    if (!campaignMatchesFilters(campaign, state.activeFilters)) return;

    state.records.unshift(campaign);
    state.meta = {
      ...state.meta,
      count: Number(state.meta?.count || 0) + 1,
    };
  },
  [types.EDIT_CAMPAIGN]: (state, campaign) => {
    const pendingActions = state.pendingActionsById || {};
    const pendingAction = pendingActions[campaign.id];
    const pauseFinished =
      pendingAction === 'pausing' && !campaign.mass_outbound_job_alive;
    const resumeStarted =
      pendingAction === 'resuming' &&
      (campaign.status_envia !== 1 || campaign.mass_outbound_job_alive);
    if (pauseFinished || resumeStarted) {
      const { [campaign.id]: _finishedAction, ...remainingActions } =
        pendingActions;
      state.pendingActionsById = remainingActions;
    }

    state.recordsById = {
      ...(state.recordsById || {}),
      [campaign.id]: campaign,
    };
    const recordIndex = state.records.findIndex(c => c.id === campaign.id);
    if (recordIndex !== -1) {
      if (campaignMatchesFilters(campaign, state.activeFilters)) {
        state.records.splice(recordIndex, 1, campaign);
      } else {
        const wasInList = state.records.some(c => c.id === campaign.id);
        state.records.splice(recordIndex, 1);
        if (
          wasInList &&
          campaign.campaign_type === state.activeFilters.campaign_type
        ) {
          state.meta = {
            ...state.meta,
            count: Math.max(0, Number(state.meta.count || 0) - 1),
          };
        }
      }
    }
  },
  [types.DELETE_CAMPAIGN]: (state, campaignId) => {
    state.records = state.records.filter(c => c.id !== campaignId);
    const { [campaignId]: _deletedCampaign, ...remainingCampaigns } =
      state.recordsById || {};
    const { [campaignId]: _deletedAction, ...remainingActions } =
      state.pendingActionsById || {};
    state.recordsById = remainingCampaigns;
    state.pendingActionsById = remainingActions;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
